import { Link } from '@inertiajs/react'
import { Truck, Phone, MapPin, Clock, MessageCircle } from 'lucide-react'
import { useT, useShared } from '../lib/i18n'
import { PHONE, WHATSAPP_URL } from '../lib/contact'
import logoUrl from '../assets/logo.png'

// lucide no longer ships brand glyphs, so use inline SVGs for socials.
function Facebook({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z" />
    </svg>
  )
}

function Instagram({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

export function StoreFooter() {
  const t = useT()
  const { nav_categories } = useShared()
  const year = new Date().getFullYear()

  return (
    <footer className="mt-20 bg-ink-950 text-ink-300">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link href="/" aria-label={t('brand')}>
              <img src={logoUrl} alt={t('brand')} className="h-16 w-auto brightness-0 invert" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-ink-400">{t('footer.tagline')}</p>
            <div className="mt-5 flex gap-3">
              <Social href="#" label="Facebook">
                <Facebook className="size-5" />
              </Social>
              <Social href="#" label="Instagram">
                <Instagram className="size-5" />
              </Social>
              <Social href={WHATSAPP_URL} label="WhatsApp">
                <MessageCircle className="size-5" />
              </Social>
            </div>
          </div>

          {/* Shop / categories */}
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-white">
              {t('footer.shop')}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {(nav_categories ?? []).map((c) => (
                <li key={c.id}>
                  <Link href={`/c/${c.slug}`} className="transition-colors hover:text-white">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-white">
              {t('footer.quick_links')}
            </h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>
                <Link href="/" className="transition-colors hover:text-white">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link href="/c/all" className="transition-colors hover:text-white">
                  {t('nav.products')}
                </Link>
              </li>
              <li>
                <Link href="/cart" className="transition-colors hover:text-white">
                  {t('nav.cart')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-white">
              {t('footer.contact')}
            </h3>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-2.5">
                <Phone className="size-4 shrink-0 text-accent-400" />
                <span className="text-ink-400">{t('footer.phone_label')}:</span>
                <a href={`tel:${PHONE.replace(/\s/g, '')}`} dir="ltr" className="font-semibold text-white hover:text-accent-300">
                  {PHONE}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <MessageCircle className="size-4 shrink-0 text-green-400" />
                <span className="text-ink-400">{t('footer.whatsapp_label')}:</span>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" dir="ltr" className="font-semibold text-white hover:text-green-300">
                  {PHONE}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <MapPin className="size-4 shrink-0 text-accent-400" />
                {t('footer.location')}
              </li>
              <li className="flex items-center gap-2.5">
                <Clock className="size-4 shrink-0 text-accent-400" />
                {t('footer.hours')}
              </li>
            </ul>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-500"
            >
              <MessageCircle className="size-4" />
              {t('footer.order_whatsapp')}
            </a>
            <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-accent-500/15 px-3 py-2 text-sm font-bold text-accent-300">
              <Truck className="size-4" />
              {t('footer.cod_note')}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-ink-800 pt-6 text-sm text-ink-500 sm:flex-row">
          <p>
            © {year} {t('brand')} — {t('footer.rights')}
          </p>
          <p>{t('footer.made_with')}</p>
        </div>
      </div>
    </footer>
  )
}

function Social({
  href,
  label,
  children,
}: {
  href: string
  label: string
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noopener noreferrer"
      className="flex size-10 items-center justify-center rounded-xl bg-ink-800 text-ink-300 transition-colors hover:bg-accent-500 hover:text-white"
    >
      {children}
    </a>
  )
}
